import { BaseMeasures } from './BaseMeasures.ts';
class SSRMeasure extends BaseMeasures {
	private startTimeOffset: number = 0;
	private startTime: number = 0;
	// fetch count (counts batched operations as one fetch)
	private fetchCount: number = 0;
	// total operations (counts individual operations even in batched operations)
	private operationsCount: number = 0;
	private errorCount: number = 0;
	private isBifrost: boolean = false;

	public fetchCountIncr(count: number = 1) {
		this.fetchCount += count;
	}

	public getFetchCount() {
		return this.fetchCount;
	}

	public errorCountIncr(count: number = 1) {
		this.errorCount += count;
	}

	public getErrorCount() {
		return this.errorCount;
	}

	public operationsCountIncr(count: number = 1) {
		this.operationsCount += count;
	}

	public getOperationsCount() {
		return this.operationsCount;
	}

	public setIsBifrost(isBifrost: boolean) {
		this.isBifrost = isBifrost;
	}

	public getIsBifrost(): boolean {
		return this.isBifrost;
	}

	/**
	 * Transform a measure name (e.g., replace 'ssr-app' with 'bifrost-ssr')
	 * Override this function to customize measure names
	 */
	public transformMeasureName(name: string): string {
		if (this.getIsBifrost() && name.includes('ssr-app/')) {
			name = name.replace('ssr-app', 'bifrost-ssr');
		}
		if (window.__SSR_REACT_STREAMING__ && name.includes('/render/')) {
			name = name.replace('render', 'stream/render');
		}
		return name;
	}

	public setStartTime(startTimeOffset: number = 0): void {
		this.startTimeOffset = startTimeOffset;
		if (!this.startTime) {
			this.startTime = Math.floor(performance.now());
		}
	}

	public getStartTime() {
		return this.startTime;
	}

	public nowFromStartTime() {
		return Math.floor(performance.now()) - this.startTime;
	}

	private now() {
		return this.nowFromStartTime() + this.startTimeOffset;
	}

	public setMeasureDuration(name: string, duration: number) {
		const transformedName = this.transformMeasureName(name);
		const entry = this.measures[transformedName];
		if (entry) {
			entry.duration = duration;
		}
	}

	public addMeasure(name: string, duration: number, offsetStartByDuration = false) {
		const transformedName = this.transformMeasureName(name);
		if (this.measures[transformedName]) {
			return;
		}
		this.measures[transformedName] = {
			startTime: offsetStartByDuration ? this.now() - duration : this.now(),
			duration,
		};
	}

	/**
	 * Adds a performance measure with explicitly provided timing data.
	 * This method is typically used when you have pre-calculated timing information
	 * (e.g., from external sources or previous measurements) rather than measuring in real-time.
	 *
	 * @param name - Identifier for the measure (will be transformed via `transformMeasureName`)
	 * @param data - Pre-calculated timing data
	 * @param data.startTime - Absolute timestamp when the measure started (from `performance.now()`)
	 * @param data.duration - Duration of the measure in milliseconds
	 *
	 * @remarks
	 * - If a measure with the same name already exists, this method does nothing (no overwrite)
	 * - `startTime` is normalized relative to SSR start time (`this.startTime`)
	 * - Both `startTime` and `duration` are floored to integers and clamped to minimum value of 0
	 * - Use this when you have complete timing data upfront, rather than `markMeasureStart`/`markMeasureEnd`
	 *
	 * @example
	 * ```typescript
	 * // Add a measure with pre-calculated timing
	 * SSRMeasures.addMeasureWithData('ssr-app/hydration', {
	 *   startTime: 1234.56,  // Absolute timestamp from performance.now()
	 *   duration: 89.12       // Duration in milliseconds
	 * });
	 * ```
	 */
	public addMeasureWithData(name: string, data: { startTime: number; duration: number }) {
		const transformedName = this.transformMeasureName(name);
		if (this.measures[transformedName]) {
			return;
		}

		this.measures[transformedName] = {
			startTime: Math.floor(Math.max(0, data.startTime) - this.startTime),
			duration: Math.floor(Math.max(0, data.duration)),
		};
	}

	override markMeasureStart(name: string) {
		const transformedName = this.transformMeasureName(name);
		if (!this.measures[transformedName]) {
			this.measures[transformedName] = {
				startTime: this.now(),
				duration: 0,
			};
		}
	}

	override markMeasureEnd(name: string) {
		const transformedName = this.transformMeasureName(name);
		const entry = this.measures[transformedName];
		if (entry) {
			entry.duration = this.now() - entry.startTime;
		}
	}

	override clearMeasures(): void {
		this.isBifrost = false;
		this.startTimeOffset = 0;
		this.startTime = 0;
		this.fetchCount = 0;
		this.errorCount = 0;
		this.operationsCount = 0;
		this.measures = {};
	}
}

export const SSRMeasures = new SSRMeasure();
